import { useState } from "react";
import { ArrowRight, CheckCircle2, Crown, Star, Shield, Clock, Users, Building2, Calendar, Globe, MapPin, Phone, Mail, Upload, FileText, DollarSign, Zap } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Premium Quote Form Page
 * - For Professional plan users who are logged in
 * - Shows enhanced premium quote form with elite contractor access
 */

const countries = [
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'TR', name: 'Türkiye' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SG', name: 'Singapore' }
];

const cities = {
  'AE': ['Dubai', 'Abu Dhabi'],
  'US': ['Las Vegas', 'Orlando', 'Chicago', 'New York', 'Atlanta'],
  'GB': ['London', 'Birmingham', 'Manchester'],
  'DE': ['Munich', 'Berlin', 'Frankfurt', 'Hamburg'],
  'TR': ['Istanbul', 'Ankara'],
  'FR': ['Paris', 'Lyon'],
  'IT': ['Milan', 'Rome'],
  'ES': ['Barcelona', 'Madrid'],
  'NL': ['Amsterdam', 'Rotterdam'],
  'SG': ['Singapore']
};

const contractorTiers = [
  { 
    id: 'elite', 
    name: 'Elite Contractors', 
    count: 12,
    badge: 'PREMIUM EXCLUSIVE',
    description: '500+ successful projects, certified for luxury brands',
    features: ['Luxury brand certified', '500+ projects completed', 'Insurance up to $5M', '24/7 support']
  },
  { 
    id: 'premium', 
    name: 'Premium Contractors', 
    count: 28,
    badge: 'RECOMMENDED',
    description: '200+ successful projects, premium quality guarantee',
    features: ['200+ projects completed', 'Premium materials only', 'Insurance up to $2M', 'Priority support']
  },
  { 
    id: 'all', 
    name: 'All Premium Network', 
    count: 67,
    badge: 'FULL ACCESS',
    description: 'All verified premium contractors in your area',
    features: ['100+ projects completed', 'Quality guaranteed', 'Insurance up to $1M', 'Standard support']
  }
];

export default function PremiumQuoteForm() {
  const [selectedTier, setSelectedTier] = useState('elite');
  const [rushProject, setRushProject] = useState(false);
  
  const [form, setForm] = useState({
    // Pre-filled from user account (simulated)
    name: 'John Smith',
    email: 'john.smith@company.com',
    company: 'Tech Innovations Inc.',
    phone: '+1 (555) 123-4567',
    
    // Project details
    eventName: '',
    eventDate: '',
    country: '',
    city: '',
    venue: '',
    standSize: '',
    standType: 'island',
    budget: '',
    currency: 'USD',
    urgency: 'normal', // normal, urgent, rush
    
    // Premium features
    dedicatedManager: true,
    insuranceCoverage: 'full',
    qualityTier: 'premium',
    
    // Project requirements
    requirements: '',
    specialRequests: '',
    brandGuidelines: '',
    deliveryDate: '',
    files: []
  });

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    console.log('Submitting premium quote:', { ...form, selectedTier, rushProject });
    alert('Premium quote submitted! Your dedicated account manager will contact you within 2 hours with elite contractor matches.');
  };

  const getResponseTime = () => {
    if (form.urgency === 'rush') return '2 hours';
    if (form.urgency === 'urgent') return '4 hours';
    return '2-4 hours';
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HEADER */}
      <header className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 relative z-50 border-b border-amber-200">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
              alt="EXPO STAND Logo" 
              className="w-12 h-10" 
            />
            <div className="text-slate-700">
              <div className="text-xl font-semibold leading-tight">EXPO</div>
              <div className="text-xl font-semibold leading-tight">STAND</div>
            </div>
          </a>
          
          <div className="flex items-center space-x-6 text-slate-700 text-sm">
            <div className="flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-orange-100 px-3 py-1.5 rounded-full">
              <Crown className="w-4 h-4 text-amber-600" />
              <span className="font-semibold text-amber-700">Professional Plan</span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>hello@expostand.com</span>
            </div>
            <span className="hover:text-amber-600 cursor-pointer">Dashboard</span>
            <span className="hover:text-amber-600 cursor-pointer">Projects</span>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                JS
              </div>
              <span className="font-medium">John Smith</span>
            </div>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-12">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Crown className="h-8 w-8 text-amber-600" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Premium Quote Request
              </h1>
            </div>
            
            <div className="grid gap-4 md:grid-cols-3 mb-8">
              <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3">
                <Clock className="h-5 w-5 text-green-600" />
                <div>
                  <div className="font-semibold text-slate-900">Response Time</div>
                  <div className="text-sm text-slate-600">{getResponseTime()}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3">
                <Shield className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-semibold text-slate-900">Full Insurance</div>
                  <div className="text-sm text-slate-600">Up to $5M coverage</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3">
                <Users className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="font-semibold text-slate-900">Dedicated Manager</div>
                  <div className="text-sm text-slate-600">Personal project guide</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONTRACTOR TIER SELECTION */}
      <section className="py-12 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Select Contractor Tier</h2>
            <p className="text-slate-600">Choose the quality level for your project contractors</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {contractorTiers.map((tier) => (
              <motion.div
                key={tier.id}
                whileHover={{ y: -2 }}
                onClick={() => setSelectedTier(tier.id)}
                className={`cursor-pointer rounded-2xl border-2 p-6 transition-all ${
                  selectedTier === tier.id
                    ? 'border-amber-500 bg-gradient-to-br from-amber-50 to-orange-50 ring-2 ring-amber-200'
                    : 'border-slate-200 bg-white hover:border-amber-300'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`text-xs font-bold px-3 py-1 rounded-full ${
                    tier.id === 'elite' ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700' :
                    tier.id === 'premium' ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700' :
                    'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700'
                  }`}>
                    {tier.badge}
                  </div>
                  <div className="text-2xl font-bold text-slate-400">{tier.count}</div>
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-2">{tier.name}</h3>
                <p className="text-slate-600 mb-4 text-sm">{tier.description}</p>
                
                <ul className="space-y-2">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PREMIUM FORM */}
      <section className="py-12">
        <div className="mx-auto max-w-6xl px-6">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Project Details</h2>
              <p className="text-slate-600">Premium contractors need detailed project information for accurate quotes</p>
            </div>

            <div className="space-y-8">
              {/* Event Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-amber-600" />
                  Event Information
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    label="Event Name"
                    placeholder="e.g., Gulfood 2025"
                    value={form.eventName}
                    onChange={(v) => updateForm('eventName', v)}
                    Icon={Building2}
                  />
                  <Field
                    label="Event Date"
                    type="date"
                    value={form.eventDate}
                    onChange={(v) => updateForm('eventDate', v)}
                    Icon={Calendar}
                  />
                  <SelectField
                    label="Country"
                    value={form.country}
                    onChange={(v) => updateForm('country', v)}
                    options={countries.map(c => ({ value: c.code, label: c.name }))}
                    Icon={Globe}
                  />
                  <SelectField
                    label="City"
                    value={form.city}
                    onChange={(v) => updateForm('city', v)}
                    options={form.country ? cities[form.country]?.map(city => ({ value: city, label: city })) || [] : []}
                    Icon={MapPin}
                    disabled={!form.country}
                  />
                  <Field
                    label="Venue"
                    placeholder="e.g., Dubai World Trade Centre"
                    value={form.venue}
                    onChange={(v) => updateForm('venue', v)}
                    Icon={Building2}
                  />
                </div>
              </div>

              {/* Stand Requirements */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Stand Requirements
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    label="Stand Size"
                    placeholder="e.g., 12m x 15m"
                    value={form.standSize}
                    onChange={(v) => updateForm('standSize', v)}
                    Icon={Building2}
                  />
                  <SelectField
                    label="Stand Type"
                    value={form.standType}
                    onChange={(v) => updateForm('standType', v)}
                    options={[
                      { value: 'inline', label: 'Inline Stand' },
                      { value: 'corner', label: 'Corner Stand' },
                      { value: 'island', label: 'Island Stand (Recommended)' },
                      { value: 'peninsula', label: 'Peninsula Stand' },
                      { value: 'custom', label: 'Custom Configuration' }
                    ]}
                    Icon={Building2}
                  />
                  <Field
                    label="Budget Range"
                    placeholder="e.g., 75,000"
                    value={form.budget}
                    onChange={(v) => updateForm('budget', v)}
                    Icon={DollarSign}
                  />
                  <SelectField
                    label="Currency"
                    value={form.currency}
                    onChange={(v) => updateForm('currency', v)}
                    options={[
                      { value: 'USD', label: 'USD ($)' },
                      { value: 'EUR', label: 'EUR (€)' },
                      { value: 'GBP', label: 'GBP (£)' },
                      { value: 'AED', label: 'AED (د.إ)' }
                    ]}
                    Icon={DollarSign}
                  />
                </div>
              </div>

              {/* Premium Options */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-600" />
                  Premium Service Options
                </h3>
                
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <SelectField
                      label="Project Urgency"
                      value={form.urgency}
                      onChange={(v) => updateForm('urgency', v)}
                      options={[
                        { value: 'normal', label: 'Normal Timeline (2-4 hours response)' },
                        { value: 'urgent', label: 'Urgent Project (4 hours response) +$500' },
                        { value: 'rush', label: 'Rush Project (2 hours response) +$1,500' }
                      ]}
                      Icon={Clock}
                    />
                    <SelectField
                      label="Insurance Coverage"
                      value={form.insuranceCoverage}
                      onChange={(v) => updateForm('insuranceCoverage', v)}
                      options={[
                        { value: 'standard', label: 'Standard Coverage ($1M)' },
                        { value: 'enhanced', label: 'Enhanced Coverage ($2M) +$300' },
                        { value: 'full', label: 'Full Coverage ($5M) +$800' }
                      ]}
                      Icon={Shield}
                    />
                  </div>

                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="h-6 w-6 text-amber-600" />
                      <div>
                        <div className="font-semibold text-amber-900">Dedicated Account Manager</div>
                        <div className="text-sm text-amber-700">Included with Professional Plan</div>
                      </div>
                    </div>
                    <p className="text-sm text-amber-800">
                      Your personal project manager will coordinate all contractors, handle communications, and ensure quality delivery. 
                      Direct WhatsApp and phone access included.
                    </p>
                  </div>
                </div>
              </div>

              {/* Detailed Requirements */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  Project Requirements
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Detailed Project Requirements
                    </label>
                    <textarea
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
                      rows={4}
                      placeholder="Describe your booth requirements, special features, technology needs, storage requirements, meeting rooms, etc."
                      value={form.requirements}
                      onChange={(e) => updateForm('requirements', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Special Requests & Preferences
                    </label>
                    <textarea
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
                      rows={3}
                      placeholder="Any specific contractor preferences, past experience requirements, or special accommodations needed"
                      value={form.specialRequests}
                      onChange={(e) => updateForm('specialRequests', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Brand Guidelines & Materials
                    </label>
                    <textarea
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
                      rows={3}
                      placeholder="Brand colors, logo usage guidelines, material preferences, sustainability requirements, etc."
                      value={form.brandGuidelines}
                      onChange={(e) => updateForm('brandGuidelines', e.target.value)}
                    />
                  </div>
                  
                  <Field
                    label="Required Completion Date"
                    type="date"
                    value={form.deliveryDate}
                    onChange={(v) => updateForm('deliveryDate', v)}
                    Icon={Clock}
                  />

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Project Files & Documents
                    </label>
                    <div className="border-2 border-dashed border-amber-300 rounded-xl p-6 text-center hover:border-amber-400 transition-colors bg-amber-50/50">
                      <Upload className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-700 font-medium">
                        Upload floor plans, brand guidelines, reference images, CAD files
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Maximum 50MB per file • Supports PDF, JPG, PNG, DWG, AI files
                      </p>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.dwg,.ai"
                        className="hidden"
                        onChange={(e) => updateForm('files', Array.from(e.target.files))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Contact Information
                  <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full font-normal">
                    Pre-filled from account
                  </span>
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    label="Full Name"
                    value={form.name}
                    onChange={(v) => updateForm('name', v)}
                    Icon={Users}
                    disabled
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(v) => updateForm('email', v)}
                    Icon={Mail}
                    disabled
                  />
                  <Field
                    label="Company"
                    value={form.company}
                    onChange={(v) => updateForm('company', v)}
                    Icon={Building2}
                    disabled
                  />
                  <Field
                    label="Phone"
                    value={form.phone}
                    onChange={(v) => updateForm('phone', v)}
                    Icon={Phone}
                  />
                </div>
              </div>

              {/* Submit Section */}
              <div className="pt-8 border-t border-slate-200">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border border-green-200">
                  <div className="flex items-start gap-3">
                    <Zap className="h-6 w-6 text-green-600 mt-1" />
                    <div>
                      <div className="font-semibold text-green-900 mb-2">Premium Quote Summary</div>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>• {contractorTiers.find(t => t.id === selectedTier)?.count} {contractorTiers.find(t => t.id === selectedTier)?.name} will receive your request</li>
                        <li>• Response time: {getResponseTime()}</li>
                        <li>• Dedicated account manager assigned</li>
                        <li>• Full insurance coverage included</li>
                        <li>• Quality guarantee with milestone protection</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold py-6 px-8 rounded-2xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 text-lg"
                >
                  <Crown className="h-6 w-6" />
                  Submit Premium Quote Request
                  <ArrowRight className="h-6 w-6" />
                </button>
                
                <p className="text-sm text-slate-500 text-center mt-4">
                  Your dedicated account manager will contact you within {getResponseTime()} with premium contractor matches
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = "text", Icon, disabled = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-4 w-4 text-slate-400" />
          </div>
        )}
        <input
          type={type}
          className={`w-full rounded-xl border border-slate-300 ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none ${disabled ? 'bg-slate-50 text-slate-500' : ''}`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, Icon, disabled = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <Icon className="h-4 w-4 text-slate-400" />
          </div>
        )}
        <select
          className={`w-full rounded-xl border border-slate-300 ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 text-slate-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none ${disabled ? 'bg-slate-50 text-slate-500' : ''} appearance-none`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
}